import { useMemo } from 'react';
import clsx from 'clsx';

import { Tag, TagSize, TagType } from '@common/atoms/Tag';
import { EventType, type Match } from '@common/clients/api';
import { useContextData } from '@common/useContextData';
import styles from '@web/molecules/FootballFormation/FootballFormation.module.scss';

import { Field } from './atoms/field/Field';
import { FieldMobile } from './atoms/field/FieldMobile';
import { TeamFormation } from './atoms/team/TeamFormation';
import { TeamLogo } from './atoms/team/TeamLogo';

export interface Props {
    match: Match;
}

const DEFAULT_FORMATION = '433';

function isAllowedEventType(eventType: EventType): eventType is 'card' | 'goal' | 'substitution' {
    return (
        eventType === EventType.CARD || eventType === EventType.GOAL || eventType === EventType.SUBSTITUTION
    );
}

export const FootballFormation = ({ match }: Props) => {
    const contextData = useContextData();

    const events = useMemo(
        () => match.details?.events?.filter((event) => isAllowedEventType(event.eventType)) || [],
        [match.details?.events],
    );
    return (
        <div className={styles.FootballFormation}>
            {match.details?.homeFormation ? (
                <div className={styles['home-team-info']}>
                    <Tag contextData={contextData} type={TagType.INVERTED} size={TagSize.MEDIUM}>
                        {match.home?.tag?.title || match.home?.name || ''}
                    </Tag>
                    <Tag contextData={contextData} type={TagType.INVERTED} size={TagSize.MEDIUM}>
                        {match.details?.homeFormation.split('').join('-')}
                    </Tag>
                </div>
            ) : null}
            <div className={clsx(styles['field'], 'field')}>
                {match.home ? <TeamLogo className={styles['home-team-logo']} team={match.home} /> : null}
                {match.away ? <TeamLogo className={styles['away-team-logo']} team={match.away} /> : null}
                <FieldMobile className={styles['field-mobile']} />
                <Field className={styles['field-desktop']} />
                <div className={styles['home-formation']}>
                    <TeamFormation
                        formation={match.details?.homeFormation || DEFAULT_FORMATION}
                        players={match?.details?.homeStartingPlayers || []}
                        events={events}
                        isHomeTeam={true}
                        shirt={match.home?.shirt}
                    />
                </div>
                <div className={styles['away-formation']}>
                    <TeamFormation
                        formation={match.details?.awayFormation || DEFAULT_FORMATION}
                        players={match?.details?.awayStartingPlayers || []}
                        events={events}
                        shirt={match.away?.shirt}
                    />
                </div>
            </div>
            {match.details?.awayFormation ? (
                <div className={styles['away-team-info']}>
                    <Tag contextData={contextData} type={TagType.INVERTED} size={TagSize.MEDIUM}>
                        {match.away?.tag?.title || match.away?.name || ''}
                    </Tag>
                    <Tag contextData={contextData} type={TagType.INVERTED} size={TagSize.MEDIUM}>
                        {match.details.awayFormation.split('').join('-')}
                    </Tag>
                </div>
            ) : null}
        </div>
    );
};
